<template>
  <div>
    <v-simple-table dense class="ta-lp-grid">
      <template v-slot:default>
        <tbody>
          <tr
            v-for="(row, ri) in tableRows"
            :key="'r' + ri"
            :class="rowClass(row)"
          >
            <td
              v-for="(cell, ci) in row"
              :key="'c' + ri + '-' + ci"
              v-if="!cell.hidden"
              :rowspan="cell.rowspan || 1"
              :colspan="cell.colspan || 1"
              class="text-caption pa-1"
              :class="cellClass(cell, row, ri)"
              :style="cellStyle(cell)"
            >
              {{ formatCellValue(cell.value) }}
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
    <div v-if="note" class="text-caption font-weight-medium mt-2 ta-lp-note">
      {{ note }}
    </div>
  </div>
</template>

<script>
import { formatDateRuIfDateString } from '@/lib/datetime'
import { trimTrailingEmptyColumns } from '@/components/expAnalogs/taLp/parseWorkbook'

export default {
  name: 'TaLpTableGrid',
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    note: {
      type: String,
      default: '',
    },
  },
  computed: {
    tableRows() {
      return trimTrailingEmptyColumns(Array.isArray(this.rows) ? this.rows : [])
    },
  },
  methods: {
    rowValues(row) {
      return (row || [])
        .map(cell => String(cell?.value ?? '').trim())
        .filter(Boolean)
    },
    isSectionRow(row) {
      const text = this.rowValues(row).join(' ').toLowerCase()
      return (
        text.includes('предлагаемый лекарственный препарат') ||
        text.includes('терапевтически аналогичные')
      )
    },
    isHeaderRow(row, rowIndex) {
      if (this.isSectionRow(row)) return false
      const text = this.rowValues(row).join(' ').toLowerCase()
      if (rowIndex <= 2 && text.includes('мнн')) return true
      if (text.includes('объемы государственных закупок')) return true
      if (text.includes('упаковки') && text.includes('млн')) return true
      return false
    },
    rowClass(row) {
      if (this.isSectionRow(row)) return 'ta-lp-row--section'
      return ''
    },
    cellClass(cell, row, rowIndex) {
      const classes = ['text-center']
      if (this.isSectionRow(row)) {
        classes.push('ta-lp-cell--section')
      } else if (this.isHeaderRow(row, rowIndex)) {
        classes.push('ta-lp-cell--header')
      } else if (this.isMergedCell(cell)) {
        classes.push('ta-lp-cell--merged')
      }
      return classes
    },
    isMergedCell(cell) {
      if (!cell) return false
      return (Number(cell.rowspan) || 1) > 1 || (Number(cell.colspan) || 1) > 1
    },
    cellStyle() {
      return {
        whiteSpace: 'pre-wrap',
        maxWidth: '280px',
        verticalAlign: 'middle',
        textAlign: 'center',
      }
    },
    formatIntegerWithSpaces(intStr) {
      const s = String(intStr ?? '').trim()
      const neg = s.startsWith('-')
      const d = neg ? s.slice(1) : s
      if (!/^\d+$/.test(d)) return s
      const grouped = d.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
      return neg ? `-${grouped}` : grouped
    },
    formatCellValue(value) {
      const raw = String(value ?? '').trim()
      if (!raw) return ''
      const formattedDate = formatDateRuIfDateString(raw)
      if (formattedDate !== raw) return formattedDate
      let m = raw.match(/^(-?\d+)[.,](\d+)$/)
      if (m) {
        const intPart = this.formatIntegerWithSpaces(m[1])
        const decPart = m[2].slice(0, 2).padEnd(2, '0')
        return `${intPart},${decPart}`
      }
      m = raw.match(/^(-?\d+)$/)
      if (m) return this.formatIntegerWithSpaces(m[1])
      return raw
    },
  },
}
</script>

<style scoped>
.ta-lp-grid >>> table {
  border-collapse: collapse;
}

.ta-lp-grid >>> td {
  border: 1px solid rgba(0, 0, 0, 0.12);
  text-align: center;
  vertical-align: middle;
}

.ta-lp-grid >>> .ta-lp-cell--header {
  font-weight: 700;
  text-align: center;
  vertical-align: middle;
}

.ta-lp-grid >>> .ta-lp-cell--section,
.ta-lp-grid >>> .ta-lp-row--section {
  background-color: rgba(0, 0, 0, 0.06);
}

.ta-lp-grid >>> .ta-lp-cell--section {
  font-weight: 700;
  text-align: center;
  vertical-align: middle;
}

.ta-lp-grid >>> .ta-lp-cell--merged {
  text-align: center;
  vertical-align: middle;
  font-weight: 700;
}

.ta-lp-note {
  white-space: pre-wrap;
}
</style>
